import React from 'react';
import ReactDOM from 'react-dom';
import { BrowserRouter as Router, Routes, Route, Navigate, Link, useLocation, useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import ErrorBoundary from './components/ErrorBoundary';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { NewFeaturesProvider, NewBadge } from './contexts/NewFeaturesContext';
import UserAvatar from './components/UserAvatar';
import { DebugProvider, useDebug } from './contexts/DebugContext';
import { AIProvider } from './contexts/AIContext';
import { LayoutProvider, ModalProvider, Modal } from './components/modal';
import DebugErrorDisplay from './components/DebugErrorDisplay';
import { DomainRouteGuard } from './components/DomainRouteGuard';
import { getNavSectionsWithItems, getDefaultLandingPage } from './config/permissionBasedNavigation';
import { getIcon } from './ui/icons';
import { useSessionContinuity } from './hooks/useSessionContinuity';
import YionaraAssistant from './domains/ai/components/YionaraAssistant';
import AICommandPalette from './domains/ai/components/AICommandPalette';
import Login from './domains/auth/pages/Login';
import ForgotPassword from './domains/auth/pages/ForgotPassword';
import ResetPassword from './domains/auth/pages/ResetPassword';
import Templates from './domains/compliance/definition/pages/Templates';
import TaskDashboard from './domains/compliance/pages/TaskDashboard';
import CapaList from './domains/compliance/pages/CapaList';
import EvidenceExpiryDashboard from './domains/compliance/pages/EvidenceExpiryDashboard';
import PolicyLibrary from './domains/compliance/pages/PolicyLibrary';
import RiskRegister from './domains/compliance/pages/RiskRegister';
import ControlsLibrary from './domains/compliance/pages/ControlsLibrary';
import GapAssessmentList from './domains/compliance/pages/GapAssessmentList';
import GapAssessmentDetail from './domains/compliance/pages/GapAssessmentDetail';
import MonitoringDashboard from './domains/compliance/pages/MonitoringDashboard';
import ManagementReportPage from './domains/compliance/pages/ManagementReportPage';
import VendorRegister from './domains/compliance/pages/VendorRegister';
import TrainingDashboard from './domains/compliance/pages/TrainingDashboard';
import AssetInventory from './domains/compliance/pages/AssetInventory';
import ComplianceTimeline from './domains/compliance/pages/ComplianceTimeline';
import TrustPortalSettings from './domains/compliance/pages/TrustPortalSettings';
import TrustPortalPublic from './domains/compliance/pages/TrustPortalPublic';
import VerifyCertificate from './domains/certification/pages/VerifyCertificate';

import Users from './domains/admin/pages/Users';
import Companies from './domains/admin/pages/Companies';
import AdminAuditPeriodsInbox from './domains/admin/pages/AdminAuditPeriodsInbox';
import AdminCompanyDetail from './domains/admin/pages/AdminCompanyDetail';
import Partners from './domains/admin/pages/Partners';
import PartnerDashboard from './domains/partner/pages/PartnerDashboard';
import PartnerCompanies from './domains/partner/pages/PartnerCompanies';
import PartnerCompanyDetail from './domains/partner/pages/PartnerCompanyDetail';
import PartnerUsers from './domains/partner/pages/PartnerUsers';
import PartnerAuditLogs from './domains/partner/pages/PartnerAuditLogs';
import PartnerBilling from './domains/partner/pages/PartnerBilling';
import CarbonCredits from './domains/partner/pages/CarbonCredits';
import ComplianceDashboard from './domains/compliance/execution/pages/ComplianceDashboard';
import Reports from './domains/reporting/pages/Reports';

import DataValidationManager from './domains/compliance/execution/pages/DataValidationManager';
import ReportingManager from './domains/reporting/pages/ReportingManager';

import TwoFactorVerify from './domains/auth/pages/TwoFactorVerify';
import AuditorEntryView from './domains/compliance/execution/components/AuditorEntryView';
import JourneyAlignedReport from './domains/reporting/components/JourneyAlignedReport';
import Messages from './domains/communication/pages/Messages';
import AccountSettings from './domains/settings/pages/AccountSettings';
import CompanySettings from './domains/companies/pages/CompanySettings';
import EmailCenter from './domains/admin/pages/EmailCenter';
import NotFound from './components/NotFound';
import PagesAdmin from './domains/pages/PagesAdmin';
import PageView from './domains/pages/PageView';
import GdprAdmin from './domains/gdpr/GdprAdmin';
import BillingAdmin from './domains/billing/BillingAdmin';
import CompanyBilling from './domains/billing/CompanyBilling';
import SettlementDashboard from './domains/billing/SettlementDashboard';
import DeveloperSettings from './domains/billing/DeveloperSettings';

import LandingPage from './components/LandingPage';
import Register from './components/Register';
import Contact from './components/Contact';
import AdminDashboard from './domains/admin/pages/AdminDashboard';
import ChangelogPage from './domains/admin/pages/ChangelogPage';
import ReleaseToast from './components/ReleaseToast';
import Documentation from './domains/learning/pages/Documentation';
import PermissionProfiles from './domains/admin/pages/PermissionProfiles';
import AccessControl from './domains/admin/pages/AccessControl';
import ChangeTrackingDashboard from './domains/admin/pages/ChangeTrackingDashboard';
import FrameworkAssignments from './domains/compliance/definition/pages/FrameworkAssignments';
import GuidanceAndProcessMaps from './domains/compliance/definition/pages/GuidanceAndProcessMaps';
import ComplianceStructureBuilder from './domains/compliance/definition/builder/ComplianceStructureBuilder';
import AuditLogs from './domains/admin/pages/AuditLogs';
import SystemLogs from './domains/admin/pages/SystemLogs';
import TroubleshootAgent from './domains/admin/pages/TroubleshootAgent';
import SystemSettings from './domains/admin/pages/SystemSettings';
import GettingStartedGuide from './domains/admin/pages/GettingStartedGuide';
import DocumentLibrary from './domains/admin/pages/DocumentLibrary';
import AIUsageDashboard from './domains/admin/pages/AIUsageDashboard';
import SystemHealth from './pages/admin/SystemHealth';
import { IssueCertification, SurveillanceAudits } from './domains/certification/pages';

import { FrameworkMapper } from './domains/compliance/definition/pages';
import { Frameworks, FrameworkLibrary } from './domains/compliance/definition/pages';

import FrameworkParser from './domains/compliance/definition/parser/FrameworkParser';
import CompanyDashboard from './domains/companies/pages/CompanyDashboard';
import CompanyFrameworkList from './domains/companies/pages/frameworks/CompanyFrameworkList';
import CompanyFrameworkDetail from './domains/companies/pages/frameworks/CompanyFrameworkDetail';
import CompanyFrameworkReview from './domains/companies/pages/frameworks/CompanyFrameworkReview';
import CompanyEvidenceLibrary from './domains/companies/pages/CompanyEvidenceLibrary';
import CompanyDocuments from './domains/companies/pages/CompanyDocuments';
import CompanyUsers from './domains/companies/pages/CompanyUsers';
import CompanyNotifications from './domains/companies/pages/CompanyNotifications';
import { CertificationDashboard } from './domains/certification/pages';
import CompanyCompliancePages from './domains/companies/pages/CompanyCompliancePages';
import AssignedPages from './domains/compliance/execution/pages/AssignedPages';
import PendingEvidence from './domains/compliance/execution/pages/PendingEvidence';
import ActiveFrameworks from './domains/compliance/execution/pages/ActiveFrameworks';
import MyUploads from './domains/compliance/execution/pages/MyUploads';
import ReferenceMaterials from './domains/compliance/execution/pages/ReferenceMaterials';
import CompliancePageView from './domains/compliance/execution/pages/CompliancePageView';
import AuditorDashboard from './domains/audit/pages/AuditorDashboard';
import ExternalAuditorDashboard from './domains/compliance/execution/pages/ExternalAuditorDashboard';
import FrameworkReviews from './domains/audit/pages/FrameworkReviews';
import EvidenceReview from './domains/audit/pages/EvidenceReview';
import PageReview from './domains/audit/pages/PageReview';
import NotesObservations from './domains/audit/pages/NotesObservations';
import NonConformities from './domains/audit/pages/NonConformities';
import AssessmentSummary from './domains/audit/pages/AssessmentSummary';
import ExportReports from './domains/reporting/pages/ExportReports';
import NotificationBell from './components/NotificationBell';
import ContextualHelp from './components/ContextualHelp';
import { HelpProvider, useHelp } from './contexts/HelpContext';
import MessageToast from './components/MessageToast';
import SessionExpiryWarning from './components/SessionExpiryWarning';
import OnboardingTour, { TourHelpButton } from './components/OnboardingTour';

import InternalAudits from './domains/compliance/pages/InternalAudits';
import ApprovalQueue from './domains/compliance/pages/ApprovalQueue';
import ManagementReviews from './domains/compliance/pages/ManagementReviews';
import ScopeDefinition from './domains/compliance/pages/ScopeDefinition';

// UK Compliance Gap modules
import ObligationRegister from './domains/compliance/pages/ObligationRegister';
import IncidentManagement from './domains/compliance/pages/IncidentManagement';
import OnboardingQuestionnaire from './domains/compliance/pages/OnboardingQuestionnaire';
import ControlTests from './domains/compliance/pages/ControlTests';
import VendorContracts from './domains/compliance/pages/VendorContracts';
import StatutoryFilings from './domains/compliance/pages/StatutoryFilings';
import ModernSlavery from './domains/compliance/pages/ModernSlavery';
import PrivacyReviews from './domains/compliance/pages/PrivacyReviews';
import DataRetention from './domains/compliance/pages/DataRetention';
import PolicyAttestations from './domains/compliance/pages/PolicyAttestations';
import TenantAuditLogs from './domains/compliance/pages/TenantAuditLogs';

import EmissionFactorsManager from './domains/emissions/pages/EmissionFactorsManager';
import ScopesDashboard from './domains/emissions/pages/ScopesDashboard';
import BulkCalculator from './domains/emissions/pages/BulkCalculator';
import TargetTracker from './domains/emissions/pages/TargetTracker';
import EmissionOffsets from './domains/emissions/pages/EmissionOffsets';
import EmissionReports from './domains/emissions/pages/EmissionReports';
import ExcelImport from './domains/emissions/pages/ExcelImport';

// New domain pages - IoT, Blockchain, Cyber Essentials, ROPA, Connectors, SSO, API Keys, Security Scanning
import IoTDashboard from './domains/admin/pages/IoTDashboard';
import BlockchainAuditDashboard from './domains/admin/pages/BlockchainAuditDashboard';
import CyberEssentials from './domains/compliance/pages/CyberEssentials';
import RopaRegister from './domains/compliance/pages/RopaRegister';
import ConnectorsDashboard from './domains/admin/pages/ConnectorsDashboard';
import SsoConfiguration from './domains/admin/pages/SsoConfiguration';
import ApiKeysManagement from './domains/admin/pages/ApiKeysManagement';
import SecurityScanningDashboard from './domains/admin/pages/SecurityScanningDashboard';
import AICapabilityManager from './domains/admin/pages/AICapabilityManager';

import './App.css';

const getCsrfToken = () => document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/)?.[1] ?? '';
const QUICK_CAPTURE_STORAGE_KEY = 'eis_quick_message_capture';

interface QuickCaptureAttachment {
  storedName?: string;
  fileName: string;
  originalName?: string;
  mimeType: string;
  size: number;
  url: string;
  uploadedAt?: string;
  isScreenshot?: boolean;
}

// Protected Route Component
// Returns 404 for unauthorized access - no "Access Denied" message
const ProtectedRoute: React.FC<{ children: React.ReactNode; requiredPermission?: string; allowedEmails?: string[] }> = ({
  children,
  requiredPermission,
  allowedEmails
}) => {
  const { isAuthenticated, user, hasPermission, isLoading } = useAuth();
  const token = localStorage.getItem('token');
  const userStr = localStorage.getItem('user');
  const hasValidToken = token && userStr;

  const validateUserSession = () => {
    if (!user || !token) return false;
    try {
      const atobPolyfill = (str: string): string => {
        if (typeof Buffer !== 'undefined') return Buffer.from(str, 'base64').toString('binary');
        if (typeof window !== 'undefined' && window.atob) return window.atob(str);
        return str.split('').map(char => String.fromCharCode(char.charCodeAt(0) + 1)).join('');
      };
      const tokenParts = token.split('.');
      if (tokenParts.length !== 3 || !tokenParts[1]) return false;
      const tokenPayload = JSON.parse(atobPolyfill(tokenParts[1]));
      const currentTime = Date.now() / 1000;
      if (tokenPayload.exp && tokenPayload.exp < currentTime - 600) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        return false;
      }
      return true;
    } catch (error) {
      return true;
    }
  };

  const isSessionValid = validateUserSession();

  if (isLoading || (hasValidToken && !user && !isAuthenticated)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-4 border-sky-700 border-t-transparent mx-auto mb-4"></div>
          <p className="text-slate-500 font-medium">Authenticating...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated || !hasValidToken || !isSessionValid) {
    return <Navigate to="/login" replace />;
  }

  // Return 404 for unauthorized access - no "Access Denied" message
  // This prevents information leakage about protected routes
  if (requiredPermission && !hasPermission(requiredPermission)) {
    return <Navigate to="/404" replace />;
  }

  // Email-level restriction for sensitive routes
  if (allowedEmails && allowedEmails.length > 0) {
    const userEmail = user?.email?.toLowerCase();
    if (!userEmail || !allowedEmails.map(e => e.toLowerCase()).includes(userEmail)) {
      return <Navigate to="/404" replace />;
    }
  }

  return <>{children}</>;
};

// Redesigned Layout Component
const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, logout, hasPermission, userDomain } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const currentPath = location.pathname;

  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const { helpOpen, openHelp, closeHelp } = useHelp();
  const [sidebarCollapsed, setSidebarCollapsed] = React.useState(() => {
    const saved = localStorage.getItem('sidebar-collapsed');
    return saved !== null ? (JSON.parse(saved) as boolean) : true;
  });

  // Save sidebar collapsed state to localStorage
  React.useEffect(() => {
    localStorage.setItem('sidebar-collapsed', JSON.stringify(sidebarCollapsed));
  }, [sidebarCollapsed]);

  const [isOnline, setIsOnline] = React.useState(navigator.onLine);
  const [userMenuOpen, setUserMenuOpen] = React.useState(false);
  const [isMasquerading, setIsMasquerading] = React.useState(false);
  const [isQuickCapturing, setIsQuickCapturing] = React.useState(false);
  const [showQuickCaptureChoice, setShowQuickCaptureChoice] = React.useState(false);
  const [quickCaptureAttachment, setQuickCaptureAttachment] = React.useState<QuickCaptureAttachment | null>(null);
  const navRef = React.useRef<HTMLElement>(null);
  const mainRef = React.useRef<HTMLElement>(null);
  const prevPathRef = React.useRef<string>('');

  React.useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Keyboard shortcut to toggle sidebar (Ctrl+B)
  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key === 'b') {
        event.preventDefault();
        setSidebarCollapsed(prev => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  React.useEffect(() => {
    const checkMasquerade = () => {
      const masqueradeTenantId = localStorage.getItem('masquerade_tenant_id');
      const impersonationToken = sessionStorage.getItem('impersonation_token');
      setIsMasquerading(!!masqueradeTenantId || !!impersonationToken);
    };
    checkMasquerade();
    const handleStorageChange = () => checkMasquerade();
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuOpen && event.target instanceof Element) {
        const userMenu = document.querySelector('[data-user-menu]');
        const dropdown = document.querySelector('.user-menu-dropdown');
        if ((!userMenu || !userMenu.contains(event.target)) && (!dropdown || !dropdown.contains(event.target))) {
          setUserMenuOpen(false);
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [userMenuOpen]);

  // Save sidebar scroll position
  React.useEffect(() => {
    const handleScroll = () => {
      if (navRef.current) {
        localStorage.setItem('sidebar-scroll-position', navRef.current.scrollTop.toString());
      }
    };
    const nav = navRef.current;
    if (nav) {
      nav.addEventListener('scroll', handleScroll);
      return () => nav.removeEventListener('scroll', handleScroll);
    }
    return undefined;
  }, []);

  // Restore sidebar scroll position
  React.useEffect(() => {
    const timer = setTimeout(() => {
      if (navRef.current) {
        const saved = localStorage.getItem('sidebar-scroll-position');
        if (saved) {
          navRef.current.scrollTop = parseInt(saved, 10);
        }
      }
    }, 100); // Small delay to ensure DOM is ready
    return () => clearTimeout(timer);
  }, []);

  // Save main content scroll position on scroll
  React.useEffect(() => {
    const handleScroll = () => {
      if (mainRef.current) {
        localStorage.setItem(`main-content-scroll-${currentPath}`, mainRef.current.scrollTop.toString());
      }
    };
    const main = mainRef.current;
    if (main) {
      main.addEventListener('scroll', handleScroll);
      return () => main.removeEventListener('scroll', handleScroll);
    }
    return undefined;
  }, [currentPath]);

  // Save and restore main content scroll position on path change
  React.useEffect(() => {
    // Save previous path's position
    if (prevPathRef.current && mainRef.current) {
      localStorage.setItem(`main-content-scroll-${prevPathRef.current}`, mainRef.current.scrollTop.toString());
    }

    // Restore new path's position
    const timer = setTimeout(() => {
      if (mainRef.current) {
        const saved = localStorage.getItem(`main-content-scroll-${currentPath}`);
        if (saved) {
          mainRef.current.scrollTop = parseInt(saved, 10);
        } else {
          mainRef.current.scrollTop = 0; // Default to top for new pages
        }
      }
    }, 100);

    prevPathRef.current = currentPath;

    return () => clearTimeout(timer);
  }, [currentPath]);

  const getRoleDisplayName = () => {
    // Check if user is in impersonation mode
    if (user?.isImpersonation) {
      return 'Impersonating';
    }

    // PRBA: Show actual permission profile names if available
    if (user?.userTypes && user.userTypes.length > 0) {
      // Prioritize permission profiles: System Admin > Company Admin > others
      const typePriority = ['System Admin', 'Company Admin', 'External Auditor', 'Internal Auditor', 'Compliance Officer'];
      const sortedTypes = user.userTypes.sort((a, b) => {
        const aIndex = typePriority.indexOf(a.name);
        const bIndex = typePriority.indexOf(b.name);
        return (aIndex === -1 ? 999 : aIndex) - (bIndex === -1 ? 999 : bIndex);
      });
      return sortedTypes[0]?.name || 'User';
    }

    // Fallback to permission-based logic if no permission profiles assigned
    if (!user?.permissions || user.permissions.length === 0) {
      return 'User';
    }

    // PRBA: Use dashboard permissions to determine role display name
    // Check for system admin first (no company_id + admin.dashboard.view)
    if (!user.company_id && user.permissions.includes('admin.dashboard.view')) {
      return 'System Admin';
    }

    // Check for external auditor (no company_id + audit.dashboard.view)
    if (!user.company_id && user.permissions.includes('audit.dashboard.view')) {
      return 'External Auditor';
    }

    // Check for internal auditor (has company_id + audit.dashboard.view)
    if (user.company_id && user.permissions.includes('audit.dashboard.view')) {
      return 'Internal Auditor';
    }

    // Check for company admin (has company_id + company.dashboard.view)
    if (user.company_id && user.permissions.includes('company.dashboard.view')) {
      return 'Company Admin';
    }

    // Check for compliance user (has company_id + compliance.dashboard.view)
    if (user.company_id && user.permissions.includes('compliance.dashboard.view')) {
      return 'Compliance User';
    }

    return 'User';
  };

  // Get permission-filtered navigation sections (filtered by domain AND permission AND email)
  const navSections = getNavSectionsWithItems(hasPermission, userDomain, user?.email, !!user?.company_id);

  // Journey guide panel was removed across all roles — the new builder-first
  // flow + role-specific dashboards make step-by-step hand-holding redundant.
  // journeyRole and showJourneyGuide are no longer referenced.

  const getMessagesRouteForDomain = React.useCallback(() => {
    switch (userDomain) {
      case 'admin':
        return '/admin/messages';
      case 'company':
        return '/company/messages';
      case 'compliance':
        return '/compliance/messages';
      case 'audit':
        return '/audit/messages';
      case 'partner':
        return '/partner/messages';
      default:
        return null;
    }
  }, [userDomain]);

  const captureScreenshotFile = React.useCallback(async () => {
    if (!navigator.mediaDevices?.getDisplayMedia) {
      throw new Error('Screenshot capture is not supported in this browser');
    }

    const stream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: false });
    try {
      const video = document.createElement('video');
      video.srcObject = stream;
      video.muted = true;
      await new Promise<void>((resolve) => {
        video.onloadedmetadata = () => resolve();
      });
      await video.play();

      const width = video.videoWidth || window.innerWidth;
      const height = video.videoHeight || window.innerHeight;
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Could not capture screen');
      ctx.drawImage(video, 0, 0, width, height);

      const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
      if (!blob) throw new Error('Could not generate screenshot file');
      return new File([blob], `screenshot-${Date.now()}.png`, { type: 'image/png' });
    } finally {
      stream.getTracks().forEach((track) => track.stop());
    }
  }, []);

  const uploadScreenshotAttachment = React.useCallback(async (file: File): Promise<QuickCaptureAttachment> => {
    const formData = new FormData();
    formData.append('file', file);

    const res = await fetch('/api/messaging/attachments', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'X-CSRF-Token': getCsrfToken(),
      },
      body: formData,
    });
    const result = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(result.error || 'Attachment upload failed');
    return result.data as QuickCaptureAttachment;
  }, []);

  const handleHeaderScreenshot = React.useCallback(async () => {
    const messagesRoute = getMessagesRouteForDomain();
    if (!messagesRoute) {
      toast.error('Messaging is not available for this account');
      return;
    }

    setIsQuickCapturing(true);
    try {
      const file = await captureScreenshotFile();
      const attachment = await uploadScreenshotAttachment(file);
      setQuickCaptureAttachment(attachment);
      setShowQuickCaptureChoice(true);
      toast.success('Screenshot captured. Choose where to send it.');
    } catch (error: any) {
      if (error?.name === 'NotAllowedError') {
        toast.info('Screenshot capture cancelled');
      } else {
        toast.error(error?.message || 'Could not capture screenshot');
      }
    } finally {
      setIsQuickCapturing(false);
    }
  }, [captureScreenshotFile, getMessagesRouteForDomain, uploadScreenshotAttachment]);

  const sendQuickCaptureToMessages = React.useCallback((mode: 'new' | 'existing') => {
    const messagesRoute = getMessagesRouteForDomain();
    if (!messagesRoute || !quickCaptureAttachment) return;

    sessionStorage.setItem(
      QUICK_CAPTURE_STORAGE_KEY,
      JSON.stringify({
        attachment: quickCaptureAttachment,
        mode,
        capturedAt: new Date().toISOString(),
        source: 'header',
      })
    );

    setShowQuickCaptureChoice(false);
    setQuickCaptureAttachment(null);
    navigate(`${messagesRoute}?quickCapture=${mode}&ts=${Date.now()}`);
  }, [getMessagesRouteForDomain, navigate, quickCaptureAttachment]);

  // Helper to render a NavLink
  const renderNavItem = (item: { id: string; title: string; icon: string; path: string }) => {
    const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
    const baseClasses = "group flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 relative";
    const activeClasses = isActive
      ? "bg-blue-50 text-blue-700 font-semibold"
      : "text-slate-600 hover:bg-slate-100 hover:text-slate-900";
    const iconClasses = isActive
      ? "text-blue-600"
      : "text-slate-400 group-hover:text-slate-600";

    // Get icon component from registry
    const IconComponent = getIcon(item.icon as any);

    return (
      <Link
        key={item.id}
        to={item.path}
        onClick={() => setMobileMenuOpen(false)}
        className={`${baseClasses} ${activeClasses}`}
        title={sidebarCollapsed ? item.title : ''}
      >
        {IconComponent && (
          <IconComponent className={`shrink-0 ${sidebarCollapsed ? 'text-xl mx-auto' : 'mr-3 text-lg'} ${iconClasses}`} />
        )}
        {!sidebarCollapsed && (
          <span className="truncate flex items-center">
            {item.title}
            {item.newFeatureId && (
              <NewBadge featureId={item.newFeatureId} title={`NEW: ${item.title}`} />
            )}
          </span>
        )}
        {isActive && !sidebarCollapsed && (
          <span className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-5 rounded-r-full bg-blue-600"></span>
        )}
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50 flex w-full">
      {/* Mobile Overlay */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-40 md:hidden bg-slate-900/50 backdrop-blur-sm transition-opacity" onClick={() => setMobileMenuOpen(false)} />
      )}

      {/* Sidebar */}
      <aside
        id="sidebar"
        className={`
          fixed top-0 left-0 z-40 h-full transition-all duration-300 ease-in-out border-r flex flex-col
          ${sidebarCollapsed ? 'w-20' : 'w-64'}
          -translate-x-full md:translate-x-0
          ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
          ${isMasquerading
            ? 'bg-white border-slate-200 border-l-4 border-l-amber-500'
            : 'bg-white border-slate-200'
          }
        `}
      >
        {/* Brand Header */}
        <div className={`h-16 flex items-center shrink-0 border-b ${isMasquerading ? 'border-slate-100' : 'border-slate-200'}`}>
          <div className="w-full flex items-center justify-center md:justify-start md:px-6">
            <svg className="shrink-0 w-8 h-8" viewBox="0 0 512 512" fill="none">
              <defs>
                <linearGradient id="sbg" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#0EA5E9"/><stop offset="100%" stopColor="#0369A1"/></linearGradient>
                <linearGradient id="slf" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#84CC16"/><stop offset="100%" stopColor="#3F6212"/></linearGradient>
              </defs>
              <path d="M256 56 C390 56 456 150 456 256" fill="none" stroke="url(#sbg)" strokeWidth="28" strokeLinecap="round"/>
              <path d="M56 256 C56 150 122 56 256 56" fill="none" stroke="url(#sbg)" strokeWidth="28" strokeLinecap="round"/>
              <circle cx="256" cy="180" r="38" fill="url(#sbg)"/>
              <path d="M170 260 Q256 340 256 320 Q256 340 342 260" fill="url(#sbg)"/>
              <path d="M256 340 Q140 340 100 420 Q180 380 256 456 Z" fill="url(#slf)"/>
              <path d="M256 340 Q372 340 412 420 Q332 380 256 456 Z" fill="url(#slf)"/>
            </svg>
            {!sidebarCollapsed && (
              <span className="ml-3 font-bold tracking-tight text-sm text-slate-800">
                EIS
              </span>
            )}
            {/* Mobile close button */}
            <button
              onClick={() => setMobileMenuOpen(false)}
              className="md:hidden ml-auto p-2 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100"
              aria-label="Close menu"
            >
              <span className="material-icons-outlined">close</span>
            </button>
          </div>
        </div>

        {/* Navigation Scroll Area */}
        <nav ref={navRef} className="flex-1 text-left overflow-y-auto overflow-x-hidden py-4 px-3 space-y-6 custom-scroll">
          {navSections.map((section) => {
            if (section.items.length === 0) return null;

            return (
              <div key={section.title}>
                {!sidebarCollapsed && (
                  <h3 className="px-3 mb-2 text-xs font-semibold uppercase tracking-wider text-slate-400">
                    {section.title}
                  </h3>
                )}
                {/* Spacer for collapsed mode to separate groups visually */}
                {sidebarCollapsed && <div className="h-px w-8 mx-auto mb-3 bg-slate-200"></div>}

                <div className="space-y-1">
                  {section.items.map(renderNavItem)}
                </div>
              </div>
            );
          })}
        </nav>

        {/* User Profile Snippet (Bottom Sidebar) */}
        {!sidebarCollapsed && (
          <div className="p-4 border-t border-slate-200">
            <div className="flex items-center gap-3">
              <UserAvatar user={user} size={32} className="bg-blue-600" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate text-slate-900">
                  {user?.first_name} {user?.last_name}
                </p>
                <p className="text-xs truncate text-slate-500">
                  {getRoleDisplayName()}
                </p>
              </div>
            </div>
          </div>
        )}
      </aside>

      {/* Main Content Wrapper */}
      <div className={`flex-1 flex flex-col h-screen w-full min-w-0 overflow-hidden transition-all duration-300 ${
        sidebarCollapsed ? 'md:ml-20' : 'md:ml-64'
      }`}>
        {/* Top Header */}
        <header className="h-16 bg-white/80 backdrop-blur-md border-b border-slate-200 sticky top-0 z-30 flex items-center justify-between px-3 sm:px-4 md:px-6 shrink-0 min-w-0">
          {/* Left: Toggle & Breadcrumbs/Search */}
          <div className="flex items-center flex-1 gap-2 sm:gap-4 min-w-0">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 -ml-1 text-slate-500 hover:text-slate-700 rounded-lg hover:bg-slate-100 shrink-0"
            >
              <span className="material-icons-outlined">menu</span>
            </button>

            <button
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className="hidden md:flex p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors shrink-0"
              title={`Toggle sidebar (${navigator.platform.includes('Mac') ? '⌘' : 'Ctrl'}+B)`}
            >
              <span className="material-icons-outlined">
                {sidebarCollapsed ? 'menu_open' : 'menu'}
              </span>
            </button>

            {/* Breadcrumb Mockup */}
            <nav className="hidden sm:flex items-center text-sm text-slate-500 overflow-hidden whitespace-nowrap min-w-0">
              <span className="text-slate-400 shrink-0 capitalize">{userDomain}</span>
              <span className="mx-2 text-slate-300 shrink-0">/</span>
              <span className="font-medium text-slate-800 truncate capitalize">
                {location.pathname.split('/').filter(Boolean).pop()?.replace(/-/g, ' ') || 'Dashboard'}
              </span>
            </nav>

            <button
              onClick={openHelp}
              className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors shrink-0"
              title="Page info"
              aria-label="Open page info"
            >
              <span className="material-icons-outlined text-[18px]">info</span>
            </button>
          </div>

          {/* Right: Search & Actions */}
          <div className="flex items-center gap-2 sm:gap-3 md:gap-5 shrink-0">
            {/* Visit Website Link */}
            <Link to="/" className="hidden sm:flex items-center gap-1.5 text-xs font-medium text-slate-400 hover:text-blue-600 transition-colors shrink-0">
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
              Website
            </Link>

            {/* Search Bar (Hidden on small mobile, responsive width) */}
            <div className="hidden xl:block relative shrink-0">
              <span className="material-icons-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-lg">search</span>
              <input
                type="text"
                placeholder="Search..."
                className="pl-10 pr-4 py-1.5 bg-slate-100 border-none rounded-full text-sm text-slate-700 focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all w-32 xl:w-48 focus:w-64"
              />
            </div>

            {/* Tour Help Button */}
            <TourHelpButton />

            {/* Help Centre */}
            <button
              onClick={() => navigate('/help')}
              className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
              title="Help Centre"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><path d="M12 17h.01"/></svg>
            </button>

            {/* Quick Screenshot */}
            <button
              onClick={() => void handleHeaderScreenshot()}
              disabled={isQuickCapturing}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 hover:text-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              title="Capture screenshot and send to messages"
              aria-label="Capture screenshot and send to messages"
            >
              <span className="material-icons-outlined text-[16px]">
                {isQuickCapturing ? 'hourglass_top' : 'photo_camera'}
              </span>
              <span className="hidden sm:inline">{isQuickCapturing ? 'Capturing...' : 'Screenshot'}</span>
            </button>

            {/* Notifications */}
            <NotificationBell />
            <MessageToast />

            {/* User Dropdown */}
            <div className="relative shrink-0" data-user-menu>
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center gap-2 focus:outline-none group"
              >
                <div className="text-right hidden lg:block">
                  <p className="text-xs font-bold text-slate-900 leading-tight group-hover:text-blue-700 transition-colors">
                    {user?.first_name} {user?.last_name}
                  </p>
                  <p className="text-[10px] text-slate-500 uppercase font-medium tracking-wide">
                    {getRoleDisplayName()}
                  </p>
                </div>
                <UserAvatar user={user} size={36} className={`border-2 transition-colors ${isMasquerading ? 'bg-amber-500 border-amber-200' : 'bg-slate-800 border-slate-200 group-hover:border-blue-500'}`} />
              </button>

              {/* Dropdown Menu */}
              {userMenuOpen && ReactDOM.createPortal(
                <div className="user-menu-dropdown fixed right-4 top-16 mt-2 w-72 max-w-[calc(100vw-2rem)] bg-white rounded-xl shadow-xl border border-slate-200 z-50 transform origin-top-right transition-all">
                  <div className="p-4 border-b border-slate-100">
                    <p className="text-sm font-bold text-slate-900">{user?.first_name} {user?.last_name}</p>
                    <p className="text-xs text-slate-500 truncate">{user?.email}</p>
                    <div className={`mt-2 inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide ${
                      isMasquerading
                        ? 'bg-amber-100 text-amber-700 border border-amber-200'
                        : 'bg-blue-50 text-blue-700 border border-blue-100'
                    }`}>
                      {isMasquerading && <span className="material-icons-outlined text-[10px] mr-1">warning</span>}
                      {getRoleDisplayName()}
                    </div>
                  </div>

                  <div className="py-2">
                    <Link to="/settings" onClick={() => setUserMenuOpen(false)} className="flex items-center px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 hover:text-blue-700">
                      <span className="material-icons-outlined text-lg mr-3 text-slate-400">settings</span>
                      Settings
                    </Link>
                    {(userDomain === 'admin' || (userDomain === 'company' && hasPermission('users.create'))) && (
                      <>
                        <div className="my-1 border-t border-slate-100"></div>
                        <Link
                          to={userDomain === 'admin' ? '/admin/users' : '/company/users'}
                          onClick={() => setUserMenuOpen(false)}
                          className="flex items-center px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 hover:text-blue-700"
                        >
                          <span className="material-icons-outlined text-lg mr-3 text-slate-400">group</span>
                          Manage Users
                        </Link>
                      </>
                    )}
                    {userDomain === 'admin' && (
                      <Link to="/admin/companies" onClick={() => setUserMenuOpen(false)} className="flex items-center px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 hover:text-blue-700">
                        <span className="material-icons-outlined text-lg mr-3 text-slate-400">corporate_fare</span>
                        Manage Companies
                      </Link>
                    )}
                  </div>

                  <div className="p-2 border-t border-slate-100 bg-slate-50 rounded-b-xl">
                    <button
                      onClick={async (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setUserMenuOpen(false);
                        if (sessionStorage.getItem('impersonation_token')) {
                          // End impersonation by closing the tab
                          sessionStorage.removeItem('impersonation_token');
                          sessionStorage.removeItem('impersonation_user');
                          window.close();
                        } else {
                          await logout();
                          navigate('/');
                        }
                      }}
                      className={`flex items-center w-full px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                        sessionStorage.getItem('impersonation_token')
                          ? 'text-amber-600 hover:bg-amber-50'
                          : 'text-red-600 hover:bg-red-50'
                      }`}
                    >
                      <span className="material-icons-outlined text-lg mr-3">
                        {sessionStorage.getItem('impersonation_token') ? 'person_off' : 'logout'}
                      </span>
                      {sessionStorage.getItem('impersonation_token') ? 'End Impersonation' : 'Logout'}
                    </button>
                  </div>
                </div>
                 , document.body)}
            </div>
          </div>
        </header>

        {/* Page Content Area */}
        <main ref={mainRef} className="flex-1 overflow-y-auto relative">
          {/* Offline Warning Banner */}
          {!isOnline && (
            <div className="sticky top-0 z-10 bg-amber-50 border-b border-amber-200 px-4 py-2 flex items-center justify-center gap-2 shadow-sm">
              <span className="material-icons-outlined text-amber-500 text-lg">wifi_off</span>
              <p className="text-sm text-amber-800 font-medium">You are currently offline. Changes may not be saved.</p>
            </div>
          )}

          <div className="py-6 sm:py-8 lg:py-10">
            <div className="w-full px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
              {children}
            </div>
          </div>
        </main>
      </div>

      {/* AI Features - Phase 2 Integration */}
      {user && (
        <>
          {/* AI Assistant Chat (also handles system update notifications) */}
          <YionaraAssistant {...(user.company_id ? { companyId: user.company_id } : {})} />

          {/* Command Palette (Cmd/Ctrl + K) */}
          <AICommandPalette />

          {/* Session expiry countdown warning */}
          <SessionExpiryWarning />
          {/* Per-persona guided onboarding tour */}
          <OnboardingTour />
        </>
      )}

      {showQuickCaptureChoice && quickCaptureAttachment && (
        <div className="fixed inset-0 bg-slate-900/60 z-[80] flex items-center justify-center p-4">
          <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100">
              <h2 className="text-base font-bold text-slate-900">Send Screenshot</h2>
              <p className="text-xs text-slate-500 mt-1">
                Your screenshot is uploaded. Choose where you want to send it.
              </p>
            </div>
            <div className="px-6 py-4 space-y-3">
              <button
                type="button"
                onClick={() => sendQuickCaptureToMessages('new')}
                className="w-full text-left px-4 py-3 rounded-xl border border-blue-200 bg-blue-50 hover:bg-blue-100 transition-colors"
              >
                <p className="text-sm font-semibold text-blue-800">Start New Message</p>
                <p className="text-xs text-blue-700 mt-0.5">Open compose and attach this screenshot automatically.</p>
              </button>
              <button
                type="button"
                onClick={() => sendQuickCaptureToMessages('existing')}
                className="w-full text-left px-4 py-3 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 transition-colors"
              >
                <p className="text-sm font-semibold text-slate-800">Reply To Existing Message</p>
                <p className="text-xs text-slate-600 mt-0.5">Go to messages, pick a thread, and send your reply with this screenshot.</p>
              </button>
            </div>
            <div className="px-6 py-3 border-t border-slate-100 flex justify-end">
              <button
                type="button"
                onClick={() => {
                  setShowQuickCaptureChoice(false);
                  setQuickCaptureAttachment(null);
                }}
                className="px-4 py-2 text-sm font-medium text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Contextual Help Panel */}
      <ContextualHelp isOpen={helpOpen} onClose={closeHelp} />
    </div>
  );
};

// Landing page redirect component
const CompanyProfileRedirect: React.FC = () => {
  const { user } = useAuth();
  if (user?.company_id) return <Navigate to={`/admin/companies/${user.company_id}`} replace />;
  return <Navigate to="/404" replace />;
};

// Domain-agnostic messaging entry point. Email notifications link here
// so the recipient lands on whichever per-domain Messages page they have
// access to, regardless of which role the sender has. Preserves the
// ?id=<n> query so the deep-link logic opens the thread automatically.
const MessagesRedirect: React.FC = () => {
  const { user, hasPermission } = useAuth();
  const location = useLocation();
  if (!user) return <Navigate to={`/login?redirect=${encodeURIComponent(location.pathname + location.search)}`} replace />;

  let target: string | null = null;
  if (!user.company_id && hasPermission('admin.dashboard.view')) target = '/admin/messages';
  else if (!user.company_id && hasPermission('partner.dashboard.view')) target = '/partner/messages';
  else if (user.company_id && hasPermission('company.dashboard.view')) target = '/company/messages';
  else if (hasPermission('audit.dashboard.view')) target = '/audit/messages';
  else if (hasPermission('compliance.dashboard.view')) target = '/compliance/messages';

  if (!target) return <Navigate to="/404" replace />;
  return <Navigate to={`${target}${location.search}`} replace />;
};

const LandingPageRedirect: React.FC = () => {
  // Always show landing page — logged-in users get a dashboard link in the header
  return <LandingPage />;
};

// Session Continuity Wrapper
const SessionContinuityWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  useSessionContinuity();
  return <>{children}</>;
};

// App Content Component with Debug Integration
// Route internal vs external auditors to different dashboards
const AuditorDashboardRouter: React.FC = () => {
  const { user } = useAuth();
  // External auditors have no company_id
  if (!user?.company_id) return <ExternalAuditorDashboard />;
  return <AuditorDashboard />;
};

const AppContent: React.FC = () => {
  const { errors, clearErrors, setDebugMode, debugMode, logApplicationError } = useDebug();

  React.useEffect(() => {
    import('./utils/debug').then(({ appErrorLogger }) => {
      appErrorLogger.setLogFunction(logApplicationError);
    });
  }, [logApplicationError]);

  return (
    <>
      <DebugErrorDisplay
        errors={errors}
        onClear={clearErrors}
        onToggleDebug={() => setDebugMode(false)}
        debugMode={debugMode}
      />
      <AuthProvider>
        <NewFeaturesProvider>
        <AIProvider>
          <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <SessionContinuityWrapper>
              <div className="App">
              <ReleaseToast />
              <Routes>
              <Route path="/" element={<LandingPageRedirect />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/page/:slug" element={<PageView />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/2fa-verify" element={<TwoFactorVerify />} />
              <Route path="/trust/:slug" element={<TrustPortalPublic />} />
              <Route path="/verify" element={<VerifyCertificate />} />
              <Route path="/verify/:code" element={<VerifyCertificate />} />

              {/* Protected Routes wrapped in new Layout */}
              {/* =====================================================
                   COMPANY DOMAIN ROUTES (Company Admin only)
                   ===================================================== */}
              <Route path="/company/dashboard" element={
                <ProtectedRoute requiredPermission="company.dashboard.view">
                  <DomainRouteGuard allowedDomain="company">
                    <Layout><CompanyDashboard /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />
              <Route path="/company/compliance-pages" element={
                <ProtectedRoute requiredPermission="company.compliance.manage">
                  <DomainRouteGuard allowedDomain="company">
                    <Layout><CompanyCompliancePages /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />
              <Route path="/company/frameworks" element={
                <ProtectedRoute requiredPermission="companies.read">
                  <DomainRouteGuard allowedDomain="company">
                    <Layout><CompanyFrameworkList /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />
              <Route path="/company/frameworks/:frameworkId" element={
                <ProtectedRoute requiredPermission="companies.read">
                  <DomainRouteGuard allowedDomain="company">
                    <Layout><CompanyFrameworkDetail /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />
              <Route path="/company/frameworks/:frameworkId/review" element={
                <ProtectedRoute requiredPermission="companies.read">
                  <DomainRouteGuard allowedDomain="company">
                    <Layout><CompanyFrameworkReview /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />
              <Route path="/company/evidence" element={
                <ProtectedRoute requiredPermission="compliance.read">
                  <DomainRouteGuard allowedDomain="company">
                    <Layout><CompanyEvidenceLibrary /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />
              <Route path="/company/documents" element={
                <ProtectedRoute requiredPermission="compliance.read">
                  <DomainRouteGuard allowedDomain="company">
                    <Layout><CompanyDocuments /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />
              <Route path="/company/users" element={
                <ProtectedRoute requiredPermission="users.read">
                  <DomainRouteGuard allowedDomain="company">
                    <Layout><CompanyUsers /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />
              <Route path="/company/permission-profiles" element={
                <ProtectedRoute requiredPermission="users.read">
                  <DomainRouteGuard allowedDomain="company">
                    <Layout><PermissionProfiles /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />

              <Route path="/company/settings" element={
                <ProtectedRoute requiredPermission="users.read">
                  <DomainRouteGuard allowedDomain="company">
                    <Layout><CompanySettings /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />
              <Route path="/company/billing" element={
                <ProtectedRoute requiredPermission="companies.read">
                  <DomainRouteGuard allowedDomain="company">
                    <Layout><CompanyBilling /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />
              {/* Redirect company admins to their company profile page */}
              <Route path="/company/profile" element={
                <ProtectedRoute requiredPermission="companies.read">
                  <DomainRouteGuard allowedDomain="company">
                    <CompanyProfileRedirect />
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />
              <Route path="/company/notifications" element={
                <ProtectedRoute requiredPermission="notifications.read">
                  <DomainRouteGuard allowedDomain="company">
                    <Layout><CompanyNotifications /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />
              <Route path="/company/messages" element={
                <ProtectedRoute requiredPermission="companies.read">
                  <DomainRouteGuard allowedDomain="company">
                    <Layout><Messages /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />
              <Route path="/company/certifications" element={
                <ProtectedRoute requiredPermission="companies.read">
                  <DomainRouteGuard allowedDomain="company">
                    <Layout><CertificationDashboard /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />
              {/* =====================================================
                   COMPLIANCE DOMAIN ROUTES (Compliance User only)
                   ===================================================== */}
              <Route path="/compliance/tasks" element={
                <ProtectedRoute requiredPermission="compliance.read">
                  <DomainRouteGuard allowedDomain={["compliance", "company"]}>
                    <Layout><AssignedPages /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />
              <Route path="/compliance/pending" element={
                <ProtectedRoute requiredPermission="compliance.read">
                  <DomainRouteGuard allowedDomain={["compliance", "company"]}>
                    <Layout><PendingEvidence /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />
              <Route path="/compliance/frameworks" element={
                <ProtectedRoute requiredPermission="frameworks.read">
                  <DomainRouteGuard allowedDomain={["compliance", "company", "audit"]}>
                    <Layout><ActiveFrameworks /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />
              <Route path="/compliance/uploads" element={
                <ProtectedRoute requiredPermission="compliance.read">
                  <DomainRouteGuard allowedDomain="compliance">
                    <Layout><MyUploads /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />
              <Route path="/compliance/reference" element={
                <ProtectedRoute requiredPermission="compliance.read">
                  <DomainRouteGuard allowedDomain="compliance">
                    <Layout><ReferenceMaterials /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />
              <Route path="/compliance/messages" element={
                <ProtectedRoute requiredPermission="compliance.read">
                  <DomainRouteGuard allowedDomain="compliance">
                    <Layout><Messages /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />
              <Route path="/compliance/pages/:id" element={
                <ProtectedRoute requiredPermission="compliance.read">
                  <DomainRouteGuard allowedDomain={["compliance", "company", "audit"]}>
                    <Layout><CompliancePageView /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />
              <Route path="/compliance/dashboard" element={
                <ProtectedRoute requiredPermission="compliance.dashboard.view">
                  <DomainRouteGuard allowedDomain="compliance">
                    <Layout><ComplianceDashboard /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />

              {/* =====================================================
                   AUDIT DOMAIN ROUTES (Auditor only)
                   ===================================================== */}
              <Route path="/audit/dashboard" element={
                <ProtectedRoute requiredPermission="audit.dashboard.view">
                  <DomainRouteGuard allowedDomain={['admin', 'audit']}>
                    <Layout><AuditorDashboardRouter /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />
              <Route path="/audit/frameworks" element={
                <ProtectedRoute requiredPermission="frameworks.read">
                  <DomainRouteGuard allowedDomain={["admin", "audit"]}>
                    <Layout><FrameworkReviews /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />
              <Route path="/audit/evidence" element={
                <ProtectedRoute requiredPermission="compliance.read">
                  <DomainRouteGuard allowedDomain={["admin", "audit"]}>
                    <Layout><EvidenceReview /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />
              {/* Phase 3: page-level internal auditor review dashboard */}
              <Route path="/audit/pages-review" element={
                <ProtectedRoute requiredPermission="compliance.review">
                  <DomainRouteGuard allowedDomain={["admin", "audit"]}>
                    <Layout><PageReview /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />
              <Route path="/audit/notes" element={
                <ProtectedRoute requiredPermission="compliance.read">
                  <DomainRouteGuard allowedDomain={["admin", "audit"]}>
                    <Layout><NotesObservations /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />
              <Route path="/audit/non-conformities" element={
                <ProtectedRoute requiredPermission="compliance.read">
                  <DomainRouteGuard allowedDomain={["admin", "audit"]}>
                    <Layout><NonConformities /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />
              <Route path="/audit/summary" element={
                <ProtectedRoute requiredPermission="compliance.read">
                  <DomainRouteGuard allowedDomain={["admin", "audit"]}>
                    <Layout><AssessmentSummary /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />
              <Route path="/audit/export" element={
                <ProtectedRoute requiredPermission="reports.export">
                  <DomainRouteGuard allowedDomain={["admin", "audit"]}>
                    <Layout><ExportReports /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />
              <Route path="/audit/messages" element={
                <ProtectedRoute requiredPermission="compliance.read">
                  <DomainRouteGuard allowedDomain={["admin", "audit"]}>
                    <Layout><Messages /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />
              {/* =====================================================
                   CERTIFICATION DOMAIN ROUTES (Auditor only)
                   ===================================================== */}

              <Route path="/audit/certifications" element={
                <ProtectedRoute requiredPermission="certifications.manage">
                  <DomainRouteGuard allowedDomain={["admin", "audit"]}>
                    <Layout><CertificationDashboard /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />

              {/*
                Admin escape hatch — bypasses the per-control review flow.
                Only System Admins can reach this. External auditors must use
                /audit/dashboard so each control gets an individual decision
                that's recorded against the certificate snapshot.
                See docs/COMPLIANCE_LIFECYCLE_PLAN.md "Admin override: manual
                certificate issuance" for full rationale.
              */}
              <Route path="/certification/issue" element={
                <ProtectedRoute requiredPermission="certifications.manage">
                  <DomainRouteGuard allowedDomain="admin">
                    <Layout><IssueCertification /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />
              <Route path="/certification/audits" element={
                <ProtectedRoute requiredPermission="certifications.manage">
                  <DomainRouteGuard allowedDomain={["admin", "audit"]}>
                    <Layout><SurveillanceAudits /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />

              {/* =====================================================
                   GOVERNANCE DOMAIN ROUTES (Super Admin only)
                   ===================================================== */}
              <Route path="/admin/dashboard" element={
                <ProtectedRoute requiredPermission="admin.dashboard.view">
                  <DomainRouteGuard allowedDomain="admin">
                    <Layout><AdminDashboard /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />
              <Route path="/changelog" element={
                <ProtectedRoute>
                  <Layout><ChangelogPage /></Layout>
                </ProtectedRoute>
              } />
              <Route path="/help" element={
                <ProtectedRoute>
                  <Layout><Documentation /></Layout>
                </ProtectedRoute>
              } />
              <Route path="/admin/change-tracking/dashboard" element={
                <ProtectedRoute requiredPermission="system.changes"> 
                  <DomainRouteGuard allowedDomain="admin"> 
                    <Layout><ChangeTrackingDashboard /></Layout> 
                  </DomainRouteGuard> 
                </ProtectedRoute> 
              } />
              <Route path="/admin/users" element={
                <ProtectedRoute requiredPermission="users.read">
                  <DomainRouteGuard allowedDomain="admin">
                    <Layout><Users /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />
              <Route path="/admin/permission-profiles" element={
                <ProtectedRoute requiredPermission="users.read">
                  <DomainRouteGuard allowedDomain={['admin', 'partner']}>
                    <Layout><PermissionProfiles /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />
              <Route path="/admin/companies" element={
                <ProtectedRoute requiredPermission="companies.read">
                  <DomainRouteGuard allowedDomain="admin">
                    <Layout><Companies /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />
              <Route path="/admin/audit-periods" element={
                <ProtectedRoute requiredPermission="admin.dashboard.view">
                  <DomainRouteGuard allowedDomain="admin">
                    <Layout><AdminAuditPeriodsInbox /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />
              <Route path="/admin/companies/:id" element={
                <ProtectedRoute requiredPermission="companies.read">
                  <DomainRouteGuard allowedDomain={['admin', 'company']}>
                    <Layout><AdminCompanyDetail /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />

              <Route path="/admin/access-control" element={
                <ProtectedRoute requiredPermission="system.config">
                  <DomainRouteGuard allowedDomain="admin">
                    <Layout><AccessControl /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />
              <Route path="/admin/email-center" element={
                <ProtectedRoute requiredPermission="system.config">
                  <DomainRouteGuard allowedDomain="admin">
                    <Layout><EmailCenter /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />
              <Route path="/admin/messages" element={
                <ProtectedRoute requiredPermission="compliance.read">
                  <DomainRouteGuard allowedDomain="admin">
                    <Layout><Messages /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />
              <Route path="/admin/audit-logs" element={
                <ProtectedRoute requiredPermission="audit_logs.view">
                  <DomainRouteGuard allowedDomain="admin">
                    <Layout><AuditLogs /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />
              <Route path="/admin/system-logs" element={
                <ProtectedRoute requiredPermission="system_logs.view">
                  <DomainRouteGuard allowedDomain="admin">
                    <Layout><SystemLogs /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />
              <Route path="/admin/troubleshoot-agent" element={
                <ProtectedRoute requiredPermission="system.config" allowedEmails={['support@yiontech.co.uk']}>
                  <DomainRouteGuard allowedDomain="admin">
                    <Layout><TroubleshootAgent /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />
              <Route path="/admin/system-settings" element={
                <ProtectedRoute requiredPermission="system.config">
                  <DomainRouteGuard allowedDomain="admin">
                    <Layout><SystemSettings /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />
              <Route path="/admin/getting-started" element={
                <ProtectedRoute requiredPermission="admin.dashboard.view">
                  <Layout><GettingStartedGuide /></Layout>
                </ProtectedRoute>
              } />
              <Route path="/admin/document-library" element={
                <ProtectedRoute requiredPermission="frameworks.read">
                  <DomainRouteGuard allowedDomain="admin">
                    <Layout><DocumentLibrary /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />
              <Route path="/admin/ai-usage" element={
                <ProtectedRoute requiredPermission="system.monitor">
                  <DomainRouteGuard allowedDomain="admin">
                    <Layout><AIUsageDashboard /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />
              <Route path="/admin/system-health" element={
                <ProtectedRoute requiredPermission="system.config">
                  <DomainRouteGuard allowedDomain="admin">
                    <Layout><SystemHealth /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />
              <Route path="/admin/pages" element={
                <ProtectedRoute requiredPermission="system.config">
                  <DomainRouteGuard allowedDomain="admin">
                    <Layout><PagesAdmin /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />
              <Route path="/admin/gdpr/*" element={
                <ProtectedRoute requiredPermission="system.config">
                  <DomainRouteGuard allowedDomain="admin">
                    <Layout><GdprAdmin /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />
              <Route path="/admin/billing" element={
                <ProtectedRoute requiredPermission="system.config">
                  <DomainRouteGuard allowedDomain="admin">
                    <Layout><BillingAdmin /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />

              <Route path="/admin/settlement" element={
                <ProtectedRoute requiredPermission="system.config">
                  <DomainRouteGuard allowedDomain="admin">
                    <Layout><SettlementDashboard /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />

              <Route path="/admin/developer-billing" element={
                <ProtectedRoute requiredPermission="system.config">
                  <DomainRouteGuard allowedDomain="admin">
                    <Layout><DeveloperSettings /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />

              {/* Partners management (system admin) */}
              <Route path="/admin/partners" element={
                <ProtectedRoute requiredPermission="system.config">
                  <DomainRouteGuard allowedDomain="admin">
                    <Layout><Partners /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />

              {/* IoT Device Management */}
              <Route path="/admin/iot" element={
                <ProtectedRoute requiredPermission="system.config">
                  <DomainRouteGuard allowedDomain="admin">
                    <Layout><IoTDashboard /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />

              {/* Blockchain Audit Trail */}
              <Route path="/admin/blockchain-audit" element={
                <ProtectedRoute requiredPermission="system.config">
                  <DomainRouteGuard allowedDomain="admin">
                    <Layout><BlockchainAuditDashboard /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />

              {/* Connectors / Integrations */}
              <Route path="/admin/connectors" element={
                <ProtectedRoute requiredPermission="system.config">
                  <DomainRouteGuard allowedDomain="admin">
                    <Layout><ConnectorsDashboard /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />

              {/* SSO Configuration */}
              <Route path="/admin/sso" element={
                <ProtectedRoute requiredPermission="system.config">
                  <DomainRouteGuard allowedDomain="admin">
                    <Layout><SsoConfiguration /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />

              {/* API Keys Management */}
              <Route path="/admin/api-keys" element={
                <ProtectedRoute requiredPermission="system.config">
                  <DomainRouteGuard allowedDomain="admin">
                    <Layout><ApiKeysManagement /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />

              {/* Security Scanning */}
              <Route path="/admin/security-scanning" element={
                <ProtectedRoute requiredPermission="system.config">
                  <DomainRouteGuard allowedDomain="admin">
                    <Layout><SecurityScanningDashboard /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />

              {/* AI Capability Manager */}
              <Route path="/admin/ai/capabilities" element={
                <ProtectedRoute requiredPermission="system.config">
                  <DomainRouteGuard allowedDomain="admin">
                    <Layout><AICapabilityManager /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />
              <Route path="/admin/ai-capabilities" element={
                <ProtectedRoute requiredPermission="system.config">
                  <DomainRouteGuard allowedDomain="admin">
                    <Layout><AICapabilityManager /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />

              {/* =====================================================
                   PARTNER ADMIN DOMAIN ROUTES
                   ===================================================== */}
              <Route path="/partner/dashboard" element={
                <ProtectedRoute requiredPermission="partner.dashboard.view">
                  <DomainRouteGuard allowedDomain="partner">
                    <Layout><PartnerDashboard /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />
              <Route path="/partner/companies" element={
                <ProtectedRoute requiredPermission="partners.manage">
                  <DomainRouteGuard allowedDomain="partner">
                    <Layout><PartnerCompanies /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />
              <Route path="/partner/companies/:id" element={
                <ProtectedRoute requiredPermission="partners.manage">
                  <DomainRouteGuard allowedDomain="partner">
                    <Layout><PartnerCompanyDetail /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />
              <Route path="/partner/users" element={
                <ProtectedRoute requiredPermission="partners.manage">
                  <DomainRouteGuard allowedDomain="partner">
                    <Layout><PartnerUsers /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />
              <Route path="/partner/audit-logs" element={
                <ProtectedRoute requiredPermission="partners.manage">
                  <DomainRouteGuard allowedDomain="partner">
                    <Layout><PartnerAuditLogs /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />
              <Route path="/partner/messages" element={
                <ProtectedRoute requiredPermission="notifications.read">
                  <DomainRouteGuard allowedDomain="partner">
                    <Layout><Messages /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />
              <Route path="/partner/billing" element={
                <ProtectedRoute requiredPermission="partners.manage">
                  <DomainRouteGuard allowedDomain="partner">
                    <Layout><PartnerBilling /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />

              {/* Carbon Credits (accessible from company and partner views) */}
              <Route path="/company/carbon-credits" element={
                <ProtectedRoute requiredPermission="compliance.read">
                  <DomainRouteGuard allowedDomain={['company', 'partner', 'admin']}>
                    <Layout><CarbonCredits /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />
              <Route path="/admin/companies/:companyId/carbon-credits" element={
                <ProtectedRoute requiredPermission="compliance.read">
                  <DomainRouteGuard allowedDomain={['admin', 'partner']}>
                    <Layout><CarbonCredits /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />

              {/* =====================================================
                   GOVERNANCE DOMAIN: COMPLIANCE DEFINITION
                   All compliance definition routes live under /compliance/definition/*
                   ===================================================== */}
              <Route path="/compliance/definition/library" element={
                <ProtectedRoute requiredPermission="frameworks.read">
                  <DomainRouteGuard allowedDomain={['admin', 'partner']}>
                    <Layout><FrameworkLibrary /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />
              <Route path="/compliance/definition/frameworks" element={
                <ProtectedRoute requiredPermission="frameworks.read">
                  <DomainRouteGuard allowedDomain={['admin', 'partner']}>
                    <Layout><Frameworks /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />
              <Route path="/compliance/definition/mapping" element={
                <ProtectedRoute requiredPermission="frameworks.update">
                  <DomainRouteGuard allowedDomain={['admin', 'partner']}>
                    <Layout><FrameworkMapper /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />
              <Route path="/compliance/definition/assignments" element={
                <ProtectedRoute requiredPermission="frameworks.assign">
                  <DomainRouteGuard allowedDomain={['admin', 'partner']}>
                    <Layout><FrameworkAssignments /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />
              <Route path="/compliance/definition/guidance" element={
                <ProtectedRoute requiredPermission="templates.update">
                  <DomainRouteGuard allowedDomain={['admin', 'partner']}>
                    <Layout><GuidanceAndProcessMaps /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />

              <Route path="/compliance/definition/parser" element={
                <ProtectedRoute requiredPermission="templates.create">
                  <DomainRouteGuard allowedDomain={['admin']}>
                    <Layout><FrameworkParser /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />
              <Route path="/compliance/definition/builder" element={
                <ProtectedRoute requiredPermission="templates.read">
                  <DomainRouteGuard allowedDomain={['admin', 'partner']}>
                    <Layout><ComplianceStructureBuilder /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />
              <Route path="/compliance/definition/templates" element={
                <ProtectedRoute requiredPermission="templates.read">
                  <DomainRouteGuard allowedDomain={['admin', 'partner']}>
                    <Layout><Templates /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />

              <Route path="/compliance/execution/data-validation" element={
                <ProtectedRoute requiredPermission="templates.update">
                  <DomainRouteGuard allowedDomain="admin">
                    <Layout><DataValidationManager /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />

              {/* Compliance Feature Routes */}
              <Route path="/compliance/task-management" element={
                <ProtectedRoute requiredPermission="compliance.read">
                  <DomainRouteGuard allowedDomain={["compliance", "company"]}>
                    <Layout><TaskDashboard /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />
              <Route path="/compliance/capa" element={
                <ProtectedRoute requiredPermission="compliance.read">
                  <DomainRouteGuard allowedDomain={["compliance", "company"]}>
                    <Layout><CapaList /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />
              <Route path="/compliance/evidence-expiry" element={
                <ProtectedRoute requiredPermission="compliance.read">
                  <DomainRouteGuard allowedDomain={["compliance", "company"]}>
                    <Layout><EvidenceExpiryDashboard /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />
              <Route path="/compliance/policies" element={
                <ProtectedRoute requiredPermission="compliance.read">
                  <DomainRouteGuard allowedDomain={["compliance", "company"]}>
                    <Layout><PolicyLibrary /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />
              <Route path="/compliance/risk-register" element={
                <ProtectedRoute requiredPermission="compliance.read">
                  <DomainRouteGuard allowedDomain={["compliance", "company"]}>
                    <Layout><RiskRegister /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />
              <Route path="/compliance/controls" element={
                <ProtectedRoute requiredPermission="compliance.read">
                  <DomainRouteGuard allowedDomain={["compliance", "company"]}>
                    <Layout><ControlsLibrary /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />
              <Route path="/compliance/gap-assessments" element={
                <ProtectedRoute requiredPermission="compliance.read">
                  <DomainRouteGuard allowedDomain={["compliance", "company"]}>
                    <Layout><GapAssessmentList /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />
              <Route path="/compliance/gap-assessment/:id" element={
                <ProtectedRoute requiredPermission="compliance.read">
                  <DomainRouteGuard allowedDomain={["compliance", "company"]}>
                    <Layout><GapAssessmentDetail /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />
              <Route path="/compliance/monitoring" element={
                <ProtectedRoute requiredPermission="compliance.read">
                  <DomainRouteGuard allowedDomain={["compliance", "company", "audit"]}>
                    <Layout><MonitoringDashboard /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />
              <Route path="/compliance/management-reports" element={
                <ProtectedRoute requiredPermission="compliance.read">
                  <DomainRouteGuard allowedDomain={["compliance", "company", "audit"]}>
                    <Layout><ManagementReportPage /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />
              <Route path="/compliance/internal-audits" element={
                <ProtectedRoute requiredPermission="compliance.read">
                  <DomainRouteGuard allowedDomain={["compliance", "company"]}>
                    <Layout><InternalAudits /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />
              <Route path="/compliance/approvals" element={
                <ProtectedRoute requiredPermission="compliance.read">
                  <DomainRouteGuard allowedDomain={["compliance", "company"]}>
                    <Layout><ApprovalQueue /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />
              <Route path="/compliance/management-reviews" element={
                <ProtectedRoute requiredPermission="compliance.read">
                  <DomainRouteGuard allowedDomain={["compliance", "company"]}>
                    <Layout><ManagementReviews /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />
              <Route path="/compliance/scope" element={
                <ProtectedRoute requiredPermission="compliance.read">
                  <DomainRouteGuard allowedDomain={["compliance", "company"]}>
                    <Layout><ScopeDefinition /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />
              <Route path="/compliance/vendor-risk" element={
                <ProtectedRoute requiredPermission="compliance.read">
                  <DomainRouteGuard allowedDomain={["compliance", "company"]}>
                    <Layout><VendorRegister /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />
              <Route path="/compliance/training" element={
                <ProtectedRoute requiredPermission="compliance.read">
                  <DomainRouteGuard allowedDomain={["compliance", "company"]}>
                    <Layout><TrainingDashboard /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />
              <Route path="/compliance/assets" element={
                <ProtectedRoute requiredPermission="compliance.read">
                  <DomainRouteGuard allowedDomain={["compliance", "company"]}>
                    <Layout><AssetInventory /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />
              <Route path="/compliance/timeline" element={
                <ProtectedRoute requiredPermission="compliance.read">
                  <DomainRouteGuard allowedDomain={["compliance", "company", "audit"]}>
                    <Layout><ComplianceTimeline /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />
              <Route path="/compliance/trust-portal" element={
                <ProtectedRoute requiredPermission="compliance.read">
                  <DomainRouteGuard allowedDomain={["compliance", "company"]}>
                    <Layout><TrustPortalSettings /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />

              {/* =====================================================
                   UK COMPLIANCE GAP MODULES
                   ===================================================== */}
              <Route path="/compliance/obligations" element={
                <ProtectedRoute requiredPermission="compliance.read">
                  <DomainRouteGuard allowedDomain={["compliance", "company"]}>
                    <Layout><ObligationRegister /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />
              <Route path="/compliance/incidents" element={
                <ProtectedRoute requiredPermission="compliance.read">
                  <DomainRouteGuard allowedDomain={["compliance", "company"]}>
                    <Layout><IncidentManagement /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />
              <Route path="/compliance/onboarding" element={
                <ProtectedRoute requiredPermission="compliance.read">
                  <DomainRouteGuard allowedDomain={["compliance", "company"]}>
                    <Layout><OnboardingQuestionnaire /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />
              <Route path="/compliance/control-tests" element={
                <ProtectedRoute requiredPermission="compliance.read">
                  <DomainRouteGuard allowedDomain={["compliance", "company"]}>
                    <Layout><ControlTests /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />
              <Route path="/compliance/vendor-contracts" element={
                <ProtectedRoute requiredPermission="compliance.read">
                  <DomainRouteGuard allowedDomain={["compliance", "company"]}>
                    <Layout><VendorContracts /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />
              <Route path="/compliance/statutory-filings" element={
                <ProtectedRoute requiredPermission="compliance.read">
                  <DomainRouteGuard allowedDomain={["compliance", "company"]}>
                    <Layout><StatutoryFilings /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />
              <Route path="/compliance/modern-slavery" element={
                <ProtectedRoute requiredPermission="compliance.read">
                  <DomainRouteGuard allowedDomain={["compliance", "company"]}>
                    <Layout><ModernSlavery /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />
              <Route path="/compliance/privacy-reviews" element={
                <ProtectedRoute requiredPermission="compliance.read">
                  <DomainRouteGuard allowedDomain={["compliance", "company"]}>
                    <Layout><PrivacyReviews /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />
              <Route path="/compliance/data-retention" element={
                <ProtectedRoute requiredPermission="compliance.read">
                  <DomainRouteGuard allowedDomain={["compliance", "company"]}>
                    <Layout><DataRetention /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />
              <Route path="/compliance/policy-attestations" element={
                <ProtectedRoute requiredPermission="compliance.read">
                  <DomainRouteGuard allowedDomain={["compliance", "company"]}>
                    <Layout><PolicyAttestations /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />
              <Route path="/compliance/company-audit-logs" element={
                <ProtectedRoute requiredPermission="compliance.read">
                  <DomainRouteGuard allowedDomain={["compliance", "company"]}>
                    <Layout><TenantAuditLogs /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />

              {/* Cyber Essentials Assessment */}
              <Route path="/compliance/cyber-essentials" element={
                <ProtectedRoute requiredPermission="compliance.read">
                  <DomainRouteGuard allowedDomain={["compliance", "company"]}>
                    <Layout><CyberEssentials /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />

              {/* ROPA - Records of Processing Activities */}
              <Route path="/compliance/ropa" element={
                <ProtectedRoute requiredPermission="compliance.read">
                  <DomainRouteGuard allowedDomain={["compliance", "company"]}>
                    <Layout><RopaRegister /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />

              <Route path="/reports" element={
                <ProtectedRoute requiredPermission="reports.read">
                  <DomainRouteGuard allowedDomain={["admin", "audit"]}>
                    <Layout><Reports /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />
              <Route path="/reporting" element={
                <ProtectedRoute requiredPermission="reports.view">
                  <DomainRouteGuard allowedDomain={["admin", "audit"]}>
                    <Layout><ReportingManager /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />
              <Route path="/auditor-view" element={
                <ProtectedRoute requiredPermission="audit_logs.view">
                  <DomainRouteGuard allowedDomain={["admin", "audit"]}>
                    <Layout><AuditorEntryView companyId={1} frameworkId="CSRD" /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />
              <Route path="/journey-report" element={
                <ProtectedRoute requiredPermission="reports.read">
                  <DomainRouteGuard allowedDomain={["admin", "audit"]}>
                    <Layout><JourneyAlignedReport reportId="1" format="pdf" /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />

              {/* =====================================================
                   SHARED ROUTES (accessible by all authenticated users)
                   ===================================================== */}
              <Route path="/settings" element={
                <ProtectedRoute>
                  <Layout><AccountSettings /></Layout>
                </ProtectedRoute>
              } />
              <Route path="/admin/settings" element={
                <ProtectedRoute requiredPermission="system.config">
                  <Layout><AccountSettings /></Layout>
                </ProtectedRoute>
              } />




              {/* =====================================================
                   EMISSIONS / CARBON MANAGEMENT ROUTES
                   ===================================================== */}
              <Route path="/emissions/factors" element={
                <ProtectedRoute requiredPermission="compliance.read">
                  <DomainRouteGuard allowedDomain={["compliance", "audit"]}>
                    <Layout><EmissionFactorsManager /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />
              <Route path="/emissions/scopes" element={
                <ProtectedRoute requiredPermission="compliance.read">
                  <DomainRouteGuard allowedDomain={["compliance", "company", "audit"]}>
                    <Layout><ScopesDashboard /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />
              <Route path="/emissions/bulk-calculator" element={
                <ProtectedRoute requiredPermission="compliance.read">
                  <DomainRouteGuard allowedDomain={["compliance", "company", "audit"]}>
                    <Layout><BulkCalculator /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />
              <Route path="/emissions/excel-import" element={
                <ProtectedRoute requiredPermission="compliance.read">
                  <DomainRouteGuard allowedDomain={["compliance", "company", "audit"]}>
                    <Layout><ExcelImport /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />
              <Route path="/emissions/targets" element={
                <ProtectedRoute requiredPermission="compliance.read">
                  <DomainRouteGuard allowedDomain={["compliance", "company", "audit"]}>
                    <Layout><TargetTracker /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />
              <Route path="/emissions/offsets" element={
                <ProtectedRoute requiredPermission="compliance.read">
                  <DomainRouteGuard allowedDomain={["compliance", "company", "audit"]}>
                    <Layout><EmissionOffsets /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />
              <Route path="/emissions/reports" element={
                <ProtectedRoute requiredPermission="compliance.read">
                  <DomainRouteGuard allowedDomain={["compliance", "company", "audit"]}>
                    <Layout><EmissionReports /></Layout>
                  </DomainRouteGuard>
                </ProtectedRoute>
              } />

              {/* Domain-agnostic messaging deep link (used by email notifications) */}
              <Route path="/messages" element={<MessagesRedirect />} />

               {/* 404 - Catch all unmatched routes */}
              <Route path="*" element={<NotFound />} />
              </Routes>
              </div>
            </SessionContinuityWrapper>
          </Router>
        </AIProvider>
        <Modal />
        </NewFeaturesProvider>
      </AuthProvider>
    </>
  );
};

function App() {
  const ErrorBoundaryComponent = ErrorBoundary;

  return (
    <LayoutProvider>
      <ModalProvider>
        <HelpProvider>
        <DebugProvider>
          <ErrorBoundaryComponent>
            <AppContent />
            <ToastContainer
              position="top-right"
              autoClose={3000}
              hideProgressBar={false}
              newestOnTop
              closeOnClick
              pauseOnHover
              theme="colored"
            />
          </ErrorBoundaryComponent>
        </DebugProvider>
        </HelpProvider>
      </ModalProvider>
    </LayoutProvider>
  );
}

export default App;
